import { useCallback, useEffect } from 'react';
import { getIn, useFormikContext } from 'formik';

import type { EMPTY_STRING } from '@e-socna/empty';
import type { DocumentTypeId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../hooks/useFormikOnNextTick.hook';
import { IdSelect } from '../../components/IdSelect';
import type { IdOption } from '../../components/IdSelect';
import type { DocumentTemplatePage_OneDocumentTemplate } from '../../pages/DocumentTemplate/hooks/useFindOneDocumentTemplateQuery.hook';

export const DocumentTypeField = () => {
  const { documentTypes, documentTypesIdMap } = useReferencesContext();

  const { errors, handleBlur, touched, values } =
    useFormikContext<DocumentTemplatePage_OneDocumentTemplate>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  useEffect(() => {
    setFieldValueOnNextTick(
      'document.kind',
      documentTypesIdMap[values.document.typeId as DocumentTypeId]?.code,
    );
  }, [documentTypesIdMap, setFieldValueOnNextTick, values.document.typeId]);

  const handleTypeIdChange = useCallback(
    (id: DocumentTypeId | typeof EMPTY_STRING) => {
      setFieldValueOnNextTick('document.typeId', id);
    },
    [setFieldValueOnNextTick],
  );

  return (
    <IdSelect<DocumentTypeId, IdOption<DocumentTypeId>>
      error={!!(errors.document?.typeId && touched.document?.typeId)}
      helperText={
        !!(errors.document?.typeId && touched.document?.typeId) &&
        (getIn(errors, 'document.typeId') as string)
      }
      id="document-type-id"
      label="Type de Document"
      name="document.typeId"
      onBlur={handleBlur}
      onChange={handleTypeIdChange}
      options={documentTypes.map((documentType) => ({
        id: documentType._id,
        label: documentType.label,
      }))}
      required
      value={values.document.typeId as DocumentTypeId}
    />
  );
};
